import { useGoToPathFactory, useScopedPathnameFactory } from '@retailer-platform/dashboard/routing'
import { routes, type DomainRouteName } from '../../routing/routes'

const SHOPPERS_ADMIN_URL =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_SHOPPERS_ADMIN_URL ?? 'https://shoppers-admin.instacart.com'

const LOGISTICS_URL =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_LOGISTICS_URL ?? 'https://logistics.instacart.com'

const CARE_ADMIN_URL =
  (window.__RPP_ENV__ as Record<string, string | undefined> | undefined)
    ?.PUBLIC_CLIENT_CARE_TOOL_ADMIN_URL ?? 'https://care.instacart.tools'

export const useDomainScopedPathname = useScopedPathnameFactory<DomainRouteName>()
export const useDomainGoToPath = useGoToPathFactory<DomainRouteName>()

export const buildCustomerUrl = (customerId?: string) =>
  routes['app-admin-customers-and-orders-ipp-customer-details'].replace(
    ':customerId',
    customerId ?? ''
  )

export const buildOrderUrl = (customerId: string, orderId: string) =>
  routes['app-admin-customers-and-orders-ipp-customer-order-details']
    .replace(':customerId', customerId)
    .replace(':orderId', orderId)

export const buildRestaurantOrderUrl = (customerId: string, orderId: string) =>
  routes['app-admin-customers-and-orders-legacy-care-restaurant-order-details']
    .replace(':customerId', customerId)
    .replace(':orderId', orderId)

export const buildBatchUrl = (batchId: string) =>
  routes['app-admin-customers-and-orders-legacy-batch-details'].replace(':batchId', batchId)

export const buildCustomerNotesUrl = (customerId: string) =>
  `${routes['app-admin-customers-and-orders-dashboard-legacy']}/customers/${customerId}/notes`

export const buildChargeLogUrl = (chargeLogId: string) =>
  `${routes['app-admin-customers-and-orders-dashboard-legacy']}/charge_logs/${chargeLogId}`

// A charge log's instrumentReference is a type-prefixed opaque string
// (`credit_<id>`, `card_<id>`, `ebt_<id>`, ...). Only credit instruments have a
// legacy /credits/<numeric id> page. Mirror the backend's
// payment_instrument_id_from_instrument_reference (require the `credit` prefix and
// take the id segment) and return null for any other instrument type so the caller
// renders plain text instead of a link to a wrong/nonexistent credit page.
export const buildCreditUrl = (instrumentReference: string): string | null => {
  const [prefix, creditId] = instrumentReference.split('_')
  if (prefix !== 'credit' || !creditId) return null
  return `${routes['app-admin-customers-and-orders-dashboard-legacy']}/credits/${creditId}`
}

export const buildShopperUrl = (shopperId: string) => `${SHOPPERS_ADMIN_URL}/shoppers/${shopperId}`

export const buildShopperBatchUrl = (batchId: string) => `${SHOPPERS_ADMIN_URL}/batches/${batchId}`

export const buildDriverBatchUrl = (batchId: string) => `${LOGISTICS_URL}/batches/${batchId}`

export const buildOrderPhotosUrl = (orderId: string) =>
  `${SHOPPERS_ADMIN_URL}/order_delivery_photos/${orderId}`

export const buildFraudMarkersUrl = (userId: string, orderId: string) =>
  `${CARE_ADMIN_URL}/fraud_markers/User/${userId}/Order/${orderId}`

/**
 * Turns an order receipt URL into its PDF variant by appending `.pdf` to the
 * `/receipt` path segment and dropping the fragment.
 *
 * e.g. `https://<domain>/orders/<id>/receipt?token=<t>&full=true#see-all`
 *   -> `https://<domain>/orders/<id>/receipt.pdf?token=<t>&full=true`
 *
 * Operates purely on the string so it works for both absolute and relative
 * receipt URLs (`new URL()` throws on relative inputs). Returns `null` only when
 * there is no receipt URL at all.
 */
export const buildReceiptPdfUrl = (receiptUrl?: string | null): string | null => {
  if (!receiptUrl) return null
  // Drop the fragment (e.g. `#see-all`); the PDF endpoint doesn't use it.
  const withoutHash = receiptUrl.split('#')[0]
  // Split path from query so we only touch the path segment.
  const queryIndex = withoutHash.indexOf('?')
  const path = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex)
  const query = queryIndex === -1 ? '' : withoutHash.slice(queryIndex)
  // Append `.pdf` to the `/receipt` segment; guard against double-appending.
  const pdfPath = path.endsWith('/receipt') ? `${path}.pdf` : path
  return `${pdfPath}${query}`
}

/** Returns full URL (origin + path) for copying or opening in new tab. */
export const buildOrderUrlFull = (customerId: string, orderId: string): string => {
  const path = buildOrderUrl(customerId, orderId)
  if (typeof window === 'undefined') return path
  const base = path.startsWith('/') ? path : `/${path}`
  return `${window.location.origin}${base}`
}
